import * as THREE from 'three';
import type {
  GlbAnalysisIssue,
  GlbMaterialFinding,
  ParsedGlbBinary,
  ParsedRuntimeGlb,
} from './types';

export interface AnalyzeMaterialsOptions {
  allowedRuntimeMaterialClasses: Set<string>;
  rejectedExtensions: Set<string>;
  allowAlphaMask: boolean;
}

function getRuntimeMaterials(runtime: ParsedRuntimeGlb): THREE.Material[] {
  const materials = new Set<THREE.Material>();

  runtime.scene?.traverse((object) => {
    if (!(object instanceof THREE.Mesh)) return;

    const meshMaterials = Array.isArray(object.material) ? object.material : [object.material];
    meshMaterials.forEach((material) => {
      if (material) {
        materials.add(material);
      }
    });
  });

  return [...materials];
}

export function analyzeMaterials(
  binary: ParsedGlbBinary,
  runtime: ParsedRuntimeGlb,
  options: AnalyzeMaterialsOptions,
): GlbMaterialFinding[] {
  const runtimeMaterials = getRuntimeMaterials(runtime);
  const count = Math.max(binary.materials.length, runtimeMaterials.length);

  return Array.from({ length: count }, (_, index) => {
    const rawMaterial = binary.materials[index] ?? {
      index,
      name: null,
      alphaMode: 'OPAQUE' as const,
      alphaCutoff: null,
      doubleSided: false,
      extensionKeys: [],
      baseColorTextureIndex: null,
      metallicRoughnessTextureIndex: null,
      normalTextureIndex: null,
      occlusionTextureIndex: null,
      emissiveTextureIndex: null,
    };
    const runtimeMaterial = runtimeMaterials[index] ?? null;
    const runtimeClass = runtimeMaterial?.type ?? runtimeMaterial?.constructor?.name ?? null;
    const issues: GlbAnalysisIssue[] = [];

    if (!runtimeClass || !options.allowedRuntimeMaterialClasses.has(runtimeClass)) {
      issues.push({
        code: 'material.runtime.unsupported',
        severity: 'error',
        message: `Unsupported runtime material class for "${rawMaterial.name ?? rawMaterial.index}": ${runtimeClass ?? 'unknown'}.`,
        path: `materials[${rawMaterial.index}]`,
      });
    }

    for (const extensionKey of rawMaterial.extensionKeys) {
      if (options.rejectedExtensions.has(extensionKey)) {
        issues.push({
          code: 'material.extension.rejected',
          severity: 'error',
          message: `Material "${rawMaterial.name ?? rawMaterial.index}" uses unsupported extension ${extensionKey}.`,
          path: `materials[${rawMaterial.index}]`,
        });
      }
    }

    if (rawMaterial.alphaMode === 'BLEND') {
      issues.push({
        code: 'material.alpha.blend',
        severity: 'error',
        message: `Material "${rawMaterial.name ?? rawMaterial.index}" uses alpha blend, which is not allowed in phase 1.`,
        path: `materials[${rawMaterial.index}]`,
      });
    }

    if (rawMaterial.alphaMode === 'MASK' && !options.allowAlphaMask) {
      issues.push({
        code: 'material.alpha.mask',
        severity: 'error',
        message: `Material "${rawMaterial.name ?? rawMaterial.index}" uses alpha mask, which is not allowed in phase 1.`,
        path: `materials[${rawMaterial.index}]`,
      });
    }

    if (rawMaterial.doubleSided) {
      issues.push({
        code: 'material.double-sided',
        severity: 'warning',
        message: `Material "${rawMaterial.name ?? rawMaterial.index}" is double-sided.`,
        path: `materials[${rawMaterial.index}]`,
      });
    }

    return {
      index: rawMaterial.index,
      name: rawMaterial.name ?? `Material ${rawMaterial.index}`,
      runtimeClass,
      alphaMode: rawMaterial.alphaMode,
      alphaCutoff: rawMaterial.alphaCutoff,
      doubleSided: rawMaterial.doubleSided,
      extensionKeys: rawMaterial.extensionKeys,
      textureSlots: {
        baseColor: rawMaterial.baseColorTextureIndex != null,
        metallicRoughness: rawMaterial.metallicRoughnessTextureIndex != null,
        normal: rawMaterial.normalTextureIndex != null,
        occlusion: rawMaterial.occlusionTextureIndex != null,
        emissive: rawMaterial.emissiveTextureIndex != null,
        alpha:
          rawMaterial.alphaMode !== 'OPAQUE' &&
          runtimeMaterial instanceof THREE.MeshStandardMaterial &&
          Boolean(runtimeMaterial.alphaMap),
      },
      issues,
    };
  });
}
